import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, ArrowLeft, AlertCircle, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  setFriendUsername,
  validateUsernameFormat,
  isUsernameDerogatory,
} from '../services/friendLookupService';

interface SetFriendUsernameProps {
  uid: string;
  email: string;
  onSuccess: () => void;
}

const SetFriendUsername = ({ uid, email, onSuccess }: SetFriendUsernameProps) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBackToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = username.trim().replace(/^@+/, '');
    if (!trimmed) {
      setError('Please enter a username');
      return;
    }

    const formatCheck = validateUsernameFormat(trimmed);
    if (!formatCheck.valid) {
      setError(formatCheck.error ?? 'Invalid username');
      return;
    }
    if (isUsernameDerogatory(trimmed)) {
      setError('This username is not allowed. Please choose another.');
      return;
    }

    setIsLoading(true);
    try {
      await setFriendUsername(uid, email, trimmed);
      onSuccess();
    } catch (err: any) {
      setError(err.message ?? 'Failed to set username. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="max-w-md mx-auto"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-full bg-secondary-100 flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-secondary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Set your friend username</h1>
            <p className="text-sm text-gray-600">
              You need a username to use the Friends feature. Friends can find you by this username or your email.
            </p>
          </div>
        </div>

        {email?.includes('privaterelay.appleid.com') && (
          <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-200 rounded-lg mb-4 text-xs text-blue-900">
            <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-950">Apple Private Relay Email</p>
              <p className="mt-0.5 text-blue-800">
                You are signed in with an Apple private relay email. Friends will find you on Cruising Along using the unique <strong>Friend Username</strong> (@username) you set below.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <div className="flex rounded-md shadow-sm">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 font-medium sm:text-sm select-none">
                @
              </span>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.replace(/^@+/, ''))}
                placeholder="cruise_fan_2024"
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:border-transparent text-gray-900 placeholder-gray-400 text-sm"
                disabled={isLoading}
                autoComplete="username"
                autoFocus
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              6–20 characters. Letters, numbers, and underscores only. Your handle will be @{username || 'username'}.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={handleBackToDashboard}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium"
              disabled={isLoading}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Dashboard
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Continue to Friends'}
            </button>
          </div>
        </form>

        <p className="mt-4 text-xs text-gray-500 text-center">
          If you don&apos;t set a username, you&apos;ll be returned to the dashboard. You can come back anytime to set one and use Friends.
        </p>
      </div>
    </motion.div>
  );
};

export default SetFriendUsername;
